/** #773 — Queue row clicks move owner position before dispatch; cloud sync when latched. */
import { useMaestroStore } from '../store/maestroStore';
import { diagnosticLog } from '../utils/diagnosticLogger';
import { getSyncHandler } from '../handlers/ClientSyncHandler';
import { emitImmediateQueueSelection } from '../handlers/queueSelectionSyncPush';
import { pushQueueSelectionToServer, trackIdFromRow } from './queueSelectionServerPush';
import {
  clearPlayUriPending,
  clearQueueOwnershipAcceptanceLatch,
  isQueueOwnershipLatched,
} from './playUriConfirmation';

let userSelectedTrackId: string | null = null;

export function resetQueueOwnerCommitForTests(): void {
  userSelectedTrackId = null;
}

export function noteUserQueueSelection(trackId: string | number | null | undefined): void {
  userSelectedTrackId = trackId != null && String(trackId) !== '' ? String(trackId) : null;
}

export function peekUserSelectedTrackId(): string | null {
  return userSelectedTrackId;
}

export function consumeUserSelectedTrackId(): string | null {
  const id = userSelectedTrackId;
  userSelectedTrackId = null;
  return id;
}

export function beaconWrongTrackDispatched(
  selectedTrackId: string,
  dispatchedTrackId: string,
  extra?: Record<string, unknown>,
): void {
  diagnosticLog('warn', 'playback', 'wrong_track_dispatched', {
    selectedTrackId,
    dispatchedTrackId,
    ...extra,
  });
}

/** True when a user selection is pending verification against the next playUri dispatch. */
export function hasPendingUserSelection(): boolean {
  return userSelectedTrackId != null;
}

export function verifyDispatchedTrackMatchesSelection(
  dispatchedTrackId: string | number | null | undefined,
  extra?: Record<string, unknown>,
): void {
  if (dispatchedTrackId == null) return;
  // One selection is verified against exactly one dispatch; a stale selection must not
  // flag every later legitimate dispatch (auto-advance, retry, resume).
  const selected = consumeUserSelectedTrackId();
  if (!selected) return;
  const dispatched = String(dispatchedTrackId);
  if (dispatched !== selected) {
    const { queueIndex, queue } = useMaestroStore.getState();
    beaconWrongTrackDispatched(selected, dispatched, {
      queuePosition: queueIndex,
      queueLength: queue.length,
      ...extra,
    });
  }
}

/**
 * Set owner queue position before any playUri dispatch.
 * Returns the track at the new index, or null when out of range.
 */
export async function commitQueueOwnerPosition(newIndex: number): Promise<{
  track: Record<string, unknown>;
  trackId: string;
  cloudSynced: boolean;
} | null> {
  const store = useMaestroStore.getState();
  const item = store.queue[newIndex];
  const track = item?.track as Record<string, unknown> | undefined;
  if (!track) return null;

  const trackId = trackIdFromRow(track);
  const latched = isQueueOwnershipLatched();

  store.setQueueIndex(newIndex);
  noteUserQueueSelection(trackId);

  clearQueueOwnershipAcceptanceLatch('user_selection');
  clearPlayUriPending('user_selection');

  let cloudSynced = false;
  if (latched) {
    emitImmediateQueueSelection(getSyncHandler(), newIndex, track);
    await pushQueueSelectionToServer(newIndex, track);
    cloudSynced = true;
  }

  diagnosticLog('info', 'playback', 'queue_owner_position_committed', {
    queueIndex: newIndex,
    trackId,
    cloudSynced,
    latched,
  });

  return { track, trackId, cloudSynced };
}
